<template>
  <ConfigProvider :locale="locale" :theme="theme">
    <div class="demo-app">
      <router-view />
    </div>
  </ConfigProvider>
</template>
<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from 'vue'
import { ConfigProvider } from 'ant-design-vue'
import { useAppLocale } from '/@/i18n/use-app-locale'

export default defineComponent({
  name: 'App',
  components: { ConfigProvider },

  setup () {
    const { antLocale } = useAppLocale()
    const fontFamilyBase = "'Noto Sans SC', 'Noto Sans', -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Segoe UI', sans-serif"

    // 实时根字号（rem.ts 动态写入 html.fontSize，这里跟随读取）
    const rootPx = ref(16)

    function syncFromRoot () {
      rootPx.value = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
    }

    onMounted(() => {
      syncFromRoot()
      window.addEventListener('resize', syncFromRoot)
    })

    onBeforeUnmount(() => {
      window.removeEventListener('resize', syncFromRoot)
    })

    // 按设计稿比例换算：设计稿基于 1920×1080，html.fontSize=16 时各 token 的基准值
    // rootPx 变化时，所有尺寸等比缩放，和业务 rem 保持同一倍率
    function sc (base: number) {
      return Math.round(base * rootPx.value / 16)
    }

    // 全局主题配置：颜色 token 固定，尺寸/字号 token 随 rootPx 动态派生
    // Table：ant-design-vue 4.x 表格走 CSS-in-js，表体背景来自 token.colorBgContainer（默认浅色）
    const theme = computed(() => ({
      token: {
        // 项目主色（品牌蓝，与 variables.scss $primary 保持一致）
        colorPrimary: '#0071E3',
        // 统一 Ant Design Vue 组件字体（覆盖组件默认字体栈）
        fontFamily: fontFamilyBase,
        colorText: '#ffffff',
        // fontSize 是 Ant 所有组件字号的"种子"，SM/LG/Heading 均由此自动派生
        fontSize: sc(14),
        // 控件高度随字号等比缩放
        controlHeight: sc(32),
        controlHeightSM: sc(24),
        controlHeightLG: sc(40),
        // 圆角随屏幕等比缩放
        borderRadius: sc(6),
        borderRadiusSM: sc(4),
        borderRadiusLG: sc(8),
      },
      components: {
        Table: {
          colorBgContainer: '#1C1C1E',  // 种子token：AntDV用于JS层颜色派生，必须保留字面量
          colorFillAlter: 'rgba(255, 255, 255, 0.07)',
          colorFillSecondary: 'rgba(255, 255, 255, 0.10)',
          colorFillContent: 'rgba(255, 255, 255, 0.14)',
          colorTextHeading: 'var(--uav-text-table)',
          colorText: 'var(--uav-text-table)',
          colorTextSecondary: 'rgba(255, 255, 255, 0.65)',
          colorTextTertiary: 'rgba(255, 255, 255, 0.45)',
          colorBorderSecondary: 'var(--uav-border)',
          colorSplit: 'var(--uav-border)',
          colorIcon: 'rgba(255, 255, 255, 0.45)',
          colorIconHover: 'var(--uav-text-basic)',
          controlItemBgActive: 'var(--uav-primary-alpha15)',
          controlItemBgActiveHover: 'var(--uav-primary-alpha22)'
        },
        Button: {
          // 登录页等特殊场景使用 LG 尺寸，高度随屏缩放
          controlHeightLG: sc(48),
          // 确保 default 按钮在所有状态下都是黑色文字
          colorText: '#000000',
          colorTextDisabled: 'rgba(0, 0, 0, 0.25)',
          defaultShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
        },
        Input: {
          // 输入框背景/文字/placeholder（支持 CSS 变量，方便全局换肤）
          colorBgContainer: 'var(--uav-input-bg)',
          colorText: 'var(--uav-input-text)',
          colorTextPlaceholder: 'var(--uav-input-placeholder)',

          // 边框 / hover / focus
          colorBorder: 'var(--uav-input-border)',
          hoverBorderColor: 'var(--uav-input-border-hover)',
          activeBorderColor: 'var(--uav-input-border-active)',
          controlOutline: 'var(--uav-input-outline)',

          // 圆角 / 高度随屏缩放
          borderRadius: sc(12),
          controlHeightLG: sc(48)
        },
        TreeSelect: {
          colorBgContainer: 'var(--uav-tree-select-bg)',
          colorText: 'var(--uav-tree-select-text)',
          colorBorder: 'var(--uav-tree-select-border)',
          hoverBorderColor: 'var(--uav-tree-select-border-hover)',
          activeBorderColor: 'var(--uav-tree-select-border-active)',
        },
        // Tree：antdv 4.x 走 CSS-in-js；选中底勿用 #2C2C2E（与侧栏 $primary-thead 同色会看不出选中）
        Tree: {
          colorBgContainer: 'transparent',
          colorText: 'var(--uav-text-basic)',
          colorTextDisabled: 'rgba(255, 255, 255, 0.35)',
          colorIcon: 'rgba(255, 255, 255, 0.45)',
          colorIconHover: 'var(--uav-text-basic)',
          controlItemBgHover: 'rgba(255, 255, 255, 0.06)',
          controlItemBgActive: 'rgba(231, 236, 242, 0.15)',
          controlItemBgActiveHover: 'var(--uav-primary-alpha22)',
        },
        // Modal：antdv 在 modal/style 里 mergeToken 第二段会强制写入 modalHeaderBg= colorBgElevated、
        // modalHeaderBorder*= lineWidth/lineType/colorSplit 等，直接写 modalHeader* 会被覆盖无效。
        // 非 wireframe 时 .ant-modal-header 默认不含 padding/border-bottom（在 genWireframeStyle 才有）。
        Modal: {
          colorBgElevated: 'var(--uav-modal-content-bg)',
          colorSplit: 'var(--uav-modal-header-border-color)',
          lineWidth: 1,
          lineType: 'solid',
          padding: sc(16),
          paddingLG: sc(24),
          borderRadiusLG: sc(8),
          colorText: 'var(--uav-text-basic)',
          colorTextHeading: 'var(--uav-text-basic)',
          colorTextDescription: 'var(--uav-text-basic)',
          colorIconHover: 'var(--uav-primary)'
        },
        // antdv 4.x Message 走 CSS-in-js，默认 colorBgElevated 为浅色；运行时会把别名 token 合并进 Message
        Message: {
          colorBgElevated: '#2C2C2E',  // 种子token，修正原 #2d2d2d 的色值漂移
          colorText: 'var(--uav-text-basic)',
          // 与 Ant Design 语义色一致：success 绿 / error 红 / warning 橙（图标为实心圆 + 白符号）
          colorSuccess: '#52c41a',
          colorError: '#ff4d4f',
          colorWarning: '#fa8c16',
          borderRadiusLG: sc(14),
          boxShadowSecondary: '0 4px 16px rgba(0, 0, 0, 0.45)'
        },
        // Steps 步骤条组件主题配置
        Steps: {
          // 已完成步骤图标背景色
          colorSuccess: 'var(--uav-primary)',
          // 连接线颜色
          colorSplit: 'var(--uav-border)',
          // 主题文字颜色
          colorText: 'var(--uav-text-basic)',
          colorTextLabel: 'var(--uav-text-basic)',
          // 次要文字颜色（已完成步骤标题）
          colorTextSecondary: 'rgba(255, 255, 255, 0.6)',
          // 描述文字颜色
          colorTextDescription: 'rgba(255, 255, 255, 0.45)',
          // 禁用文字颜色
          colorTextDisabled: 'rgba(255, 255, 255, 0.3)',
          // 等待状态图标背景色
          colorFillContent: '#2C2C2E',  // 种子token
          // 等待状态图标边框色
          colorBorder: 'var(--uav-border)',
          // 错误状态颜色
          colorError: 'var(--uav-danger)',
        },
        // Progress 进度条组件主题配置
        Progress: {
          // 默认进度条颜色（未指定状态时）
          colorInfo: 'var(--uav-primary)',
          // 完成状态颜色
          colorSuccess: '#28d445',
          // 进行中状态的颜色
          colorProcessing: 'var(--uav-primary)',
          // 未完成状态的轨道背景色
          colorFillSecondary: 'rgba(255, 255, 255, 0.08)',
          // 进度条文本颜色
          colorText: 'var(--uav-text-table)',
          // 圆角
          borderRadius: sc(4)
        }
      }
    }))

    return {
      locale: antLocale,
      theme
    }
  }
})
</script>
<style lang="scss" scoped>
.demo-app {
  width: 100%;
  height: 100%;

  .map-wrapper {
    height: 100%;
    width: 100%;
  }
}
</style>

<style lang="scss">
#demo-app {
  width: 100%;
  height: 100%;
}
</style>
